// frontend/src/features/ups/BannedDriversList.js
// This React component renders the UPS banned drivers table, honoring global agency/search filters,
// live-syncing via Socket.IO, and letting admins unban drivers or view/edit ban details in a modal.

import React, { useEffect, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { UserCheck, Eye } from 'lucide-react';
import socket from '../../services/socket';

import { selectIsUpsAdmin } from '../auth/authSlice';
import { selectAllAgencies } from '../agencies/agenciesSlice';
import {
  makeSelectDriversForTab,
  upsertDriver,
  removeDriver,
  applyStatusChange,
} from '../drivers/driverSlice';
import { selectFilters } from '../ui/filtersSlice';

import { unbanDriver } from '../../services/upsDriverService';
import BannedDriverEditModal from './BannedDriverEditModal';

const normalizeId = v => (v == null ? undefined : Number(v));

export default function BannedDriversList() {
  const dispatch = useDispatch();
  const isAdmin  = useSelector(selectIsUpsAdmin);

  // Global filters/state
  const { agencyId, q } = useSelector(selectFilters);
  const agencies        = useSelector(selectAllAgencies);

  // List selector: only Banned, but respects global agency/search
  const selectBanned = useMemo(() => makeSelectDriversForTab('Banned'), []);
  const drivers      = useSelector(selectBanned);

  // Focus-on-arrival via ?focus=<id>
  const [sp, setSp] = useSearchParams();
  useEffect(() => {
    const focus = sp.get('focus');
    if (!focus) return;
    const el = document.querySelector(`[data-row-id="${focus}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el.classList.add('ring-2','ring-blue-400','animate-pulse');
    const t = setTimeout(() => {
      el.classList.remove('ring-2','ring-blue-400','animate-pulse');
      const next = new URLSearchParams(sp);
      next.delete('focus');
      setSp(next, { replace: true });
    }, 1600);
    return () => clearTimeout(t);
  }, [sp, setSp]);

  // Modal state
  const [showEdit, setShowEdit] = useState(false);
  const [selectedBan, setSelectedBan] = useState(null);

  // Realtime sockets (patch-only for lean events)
  useEffect(() => {
    const handleUpsert   = d => dispatch(upsertDriver({ ...d, id: normalizeId(d?.id) }));
    const handleDeleted  = ({ id }) => dispatch(removeDriver(normalizeId(id)));
    const handleBanned   = p => {
      const id = normalizeId(p?.id ?? p?.driver_id);
      if (id) dispatch(applyStatusChange({ id, status: 'Banned' }));
    };
    const handleUnbanned = p => {
      const id = normalizeId(p?.id ?? p?.driver_id);
      if (id) dispatch(applyStatusChange({ id, status: p?.status ?? 'Reserved' }));
    };
    const handleStatusChanged = ({ id, status }) => {
      const nId = normalizeId(id);
      if (nId) dispatch(applyStatusChange({ id: nId, status }));
    };

    socket.on('driverAdded', handleUpsert);
    socket.on('driverUpdated', handleUpsert);
    socket.on('driverDeleted', handleDeleted);
    socket.on('driverBanned', handleBanned);
    socket.on('driverUnbanned', handleUnbanned);
    socket.on('driver:statusChanged', handleStatusChanged);

    return () => {
      socket.off('driverAdded', handleUpsert);
      socket.off('driverUpdated', handleUpsert);
      socket.off('driverDeleted', handleDeleted);
      socket.off('driverBanned', handleBanned);
      socket.off('driverUnbanned', handleUnbanned);
      socket.off('driver:statusChanged', handleStatusChanged);
    };
  }, [dispatch]);

  // Actions
  const handleUnban = async row => {
    try {
      // Prefer ban_id; backend is compatible with driver_id fallback but ban_id is correct
      const idForApi = row.ban_id ?? row.id;
      const updated = await unbanDriver(idForApi);
      // Server returns full driver DTO (now Reserved) — safe to upsert
      dispatch(upsertDriver(updated));
    } catch (err) {
      console.error('Unban failed:', err);
    }
  };

  // Modal helpers
  const openEdit  = row => { setSelectedBan(row); setShowEdit(true); };
  const closeEdit = () => { setShowEdit(false); setSelectedBan(null); };
  const handleBanUpdated = updated => { if (updated) dispatch(upsertDriver(updated)); };

  // Empty-state labels
  const scopeLabel = (() => {
    if (!agencyId) return 'All Agencies';
    const a = (agencies || []).find(x => String(x.id ?? x.agency_id ?? x.agencyId) === String(agencyId));
    return a?.name ?? a?.agency_name ?? `Agency #${agencyId}`;
  })();
  const hasSearch = (q || '').trim().length > 0;

  return (
    <>
      <div className="overflow-x-auto my-6">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">Banned Drivers</h3>
        <table className="min-w-full bg-white rounded-lg shadow overflow-hidden">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              {['Name','Vehicle Reg.','Phone','Email','Agency','Banned At','Actions'].map(col => (
                <th key={col} className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase">{col}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {drivers.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  No banned drivers{agencyId ? ` for ${scopeLabel}` : ''}{hasSearch ? ` matching “${q}”` : ''}.
                </td>
              </tr>
            ) : drivers.map(driver => (
              <tr key={driver.id} data-row-id={driver.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">{driver.name}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {driver.vehicle_registration_number || driver.vehicleRegistrationNumber || driver.vrn || '—'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.phone}</td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.email}</td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.agency_name || driver.agency?.name || '—'}</td>
                <td className="px-6 py-4 whitespace-nowrap">{driver.banned_at ? new Date(driver.banned_at).toLocaleString() : '—'}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handleUnban(driver)}
                      disabled={!isAdmin}
                      title={isAdmin ? 'Unban (moves to Reserved)' : 'Admins only'}
                      className={'px-2 py-1 rounded text-green-600 ' + (isAdmin ? 'hover:bg-gray-100' : 'bg-gray-200 text-gray-400 cursor-not-allowed')}
                    >
                      <UserCheck size={16} />
                    </button>
                    <button
                      onClick={() => openEdit(driver)}
                      className="px-2 py-1 rounded hover:bg-gray-100 text-blue-600"
                      title="View details"
                    >
                      <Eye size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showEdit && selectedBan && (
        <BannedDriverEditModal
          ban={selectedBan}
          onClose={closeEdit}
          onUpdate={handleBanUpdated}
        />
      )}
    </>
  );
}
